/**
 * <bp-user-menu> — navbar user menu: guest vs authenticated.
 *
 * The server renders both states; this element picks which one to reveal
 * based on auth tokens in localStorage (shared/auth.ts is the source of
 * truth for keys and logout semantics).
 */

import { isAuthenticated, getStoredUser, logout } from '../shared/auth.ts';

interface StoredUser {
  name?: string | null;
  email?: string | null;
  admin?: boolean | null;
}

export class BpUserMenu extends HTMLElement {
  private guestEl: HTMLElement | null = null;
  private authedEl: HTMLElement | null = null;
  private pill: HTMLButtonElement | null = null;
  private menu: HTMLElement | null = null;
  private signoutBtn: HTMLButtonElement | null = null;

  connectedCallback() {
    this.guestEl = this.querySelector<HTMLElement>('.bp-user-guest');
    this.authedEl = this.querySelector<HTMLElement>('.bp-user-authed');
    this.pill = this.querySelector<HTMLButtonElement>('.bp-user-avatar-btn');
    this.menu = this.querySelector<HTMLElement>('.bp-user-menu');
    this.signoutBtn = this.querySelector<HTMLButtonElement>('.bp-user-menu-signout');

    this.hydrate();

    this.pill?.addEventListener('click', this.onPillClick);
    this.signoutBtn?.addEventListener('click', this.onSignOut);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.pill?.removeEventListener('click', this.onPillClick);
    this.signoutBtn?.removeEventListener('click', this.onSignOut);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private hydrate() {
    if (!isAuthenticated()) {
      this.showGuest();
      return;
    }
    this.showAuthed((getStoredUser() as StoredUser | null) ?? {});
  }

  private showGuest() {
    if (this.guestEl) this.guestEl.hidden = false;
    if (this.authedEl) this.authedEl.hidden = true;
  }

  private showAuthed(user: StoredUser) {
    if (this.guestEl) this.guestEl.hidden = true;
    if (!this.authedEl) return;
    this.authedEl.hidden = false;

    const displayName = user.name?.trim() || emailLocalPart(user.email) || 'User';
    const handle = user.email ?? '';
    const initials = initialsFor(user.name, user.email);
    const isAdmin = user.admin === true;

    this.setText('.bp-user-menu-name', displayName);
    if (handle) this.setText('.bp-user-menu-handle', handle);

    this.querySelectorAll<HTMLElement>('.bp-user-avatar').forEach((el) => {
      el.textContent = initials;
    });

    this.authedEl.classList.toggle('is-admin', isAdmin);
    this.querySelectorAll<HTMLElement>('[data-admin-only]').forEach((el) => {
      el.hidden = !isAdmin;
    });
  }

  private onPillClick = (event: MouseEvent) => {
    event.stopPropagation();
    this.setOpen(!this.isOpen);
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.setOpen(false);
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.setOpen(false);
      this.pill?.focus();
    }
  };

  private onSignOut = (event: MouseEvent) => {
    event.preventDefault();
    void logout();
  };

  private get isOpen() {
    return this.pill?.getAttribute('aria-expanded') === 'true';
  }

  private setOpen(open: boolean) {
    if (!this.pill || !this.menu) return;
    this.pill.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      this.menu.removeAttribute('hidden');
    } else {
      this.menu.setAttribute('hidden', '');
    }
  }

  private setText(selector: string, text: string) {
    const el = this.querySelector<HTMLElement>(selector);
    if (el) el.textContent = text;
  }
}

function emailLocalPart(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.indexOf('@');
  return at === -1 ? email : email.slice(0, at);
}

function initialsFor(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name && name.trim()) || emailLocalPart(email) || '';
  const chars = source.replace(/\s+/g, '').toUpperCase();
  return chars.slice(0, 2) || '··';
}

if (!customElements.get('bp-user-menu')) {
  customElements.define('bp-user-menu', BpUserMenu);
}
